import {
    computed,
    defineComponent,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
} from "vue";
import { useRoute } from "vue-router";
import PwaInstallButton from "./PwaInstallButton.js";
import { useLocale } from "../../locales/i18n.js";
import Store from "../../store.js";
import {
    createLogscordPublicSocialLinks,
    DefaultLogscordPublicExternalUrls,
    LogscordLandingPreviewMetrics,
    LogscordLandingPreviewSidebarItems,
    LogscordLandingTimelineRows,
    withPublicLocalePrefix,
    type PublicLocaleCode,
} from "@logscord/public-site-config";


//////////////////////////////////////////////////
//  PUBLIC SHELL
//////////////////////////////////////////////////


const PublicPrimaryLinks = [
    { to: "/features", icon: "auto_awesome", labelKey: "public.nav.features" },
    { to: "/servers", icon: "dns", labelKey: "public.nav.servers" },
    { to: "/docs", icon: "menu_book", labelKey: "public.nav.docs" },
] as const;

const PublicSecondaryLinks = [
    { to: "/vision", icon: "lightbulb", labelKey: "public.nav.vision" },
    { to: "/compare", icon: "compare_arrows", labelKey: "public.nav.compare" },
    { to: "/trust", icon: "verified", labelKey: "public.nav.trust" },
    { to: "/team", icon: "group", labelKey: "public.nav.team" },
] as const;

const PublicLinks = [
    ...PublicPrimaryLinks,
    ...PublicSecondaryLinks,
] as const;

const PublicLegalLinks = [
    { to: "/terms", icon: "contract", labelKey: "public.nav.terms" },
    { to: "/privacy", icon: "privacy_tip", labelKey: "public.nav.privacy" },
    { to: "/security", icon: "shield_lock", labelKey: "public.nav.security" },
    { to: "/disponibility", icon: "cloud_done", labelKey: "public.nav.disponibility" },
] as const;

const PublicFooterSections = [
    { labelKey: "public.footer.product", links: PublicPrimaryLinks },
    { labelKey: "public.nav.discover", links: PublicSecondaryLinks },
    { labelKey: "public.footer.legal", links: PublicLegalLinks },
] as const;

const PublicExternalUrls = {
    github  : import.meta.env["VITE_PUBLIC_GITHUB_URL"] || DefaultLogscordPublicExternalUrls.github,
    linkedin: import.meta.env["VITE_PUBLIC_LINKEDIN_URL"] || DefaultLogscordPublicExternalUrls.linkedin,
    npm     : import.meta.env["VITE_PUBLIC_NPM_URL"] || DefaultLogscordPublicExternalUrls.npm,
    topgg   : import.meta.env["VITE_PUBLIC_TOPGG_URL"] || DefaultLogscordPublicExternalUrls.topgg,
    discord : import.meta.env["VITE_PUBLIC_DISCORD_URL"] || DefaultLogscordPublicExternalUrls.discord,
} as const;

const PublicSocialLinks = createLogscordPublicSocialLinks(PublicExternalUrls);

function renderSocialIcon(icon: typeof PublicSocialLinks[number]["icon"]) {
    if (icon === "linkedin")
        return (
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28ZM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12ZM7.12 20.45H3.55V9h3.57v11.45ZM22.23 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45C23.2 24 24 23.23 24 22.27V1.73C24 .77 23.2 0 22.22 0h.01Z" />
            </svg>
        );

    if (icon === "npm")
        return (
            <svg class="landing-footer-social-icon-npm" viewBox="0 0 16 16" aria-hidden="true">
                <path fill="currentColor" d="M0 16V0h16v16H0ZM3 3v10h5V5h3v8h2V3H3Z" />
                <path fill="transparent" d="M3 3h10v10h-2V5H8v8H3V3Z" />
            </svg>
        );

    if (icon === "github")
        return (
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.39 7.86 10.91.58.11.79-.25.79-.56v-2.01c-3.2.7-3.87-1.54-3.87-1.54-.52-1.33-1.28-1.68-1.28-1.68-1.05-.72.08-.71.08-.71 1.16.08 1.77 1.19 1.77 1.19 1.03 1.76 2.7 1.25 3.36.96.1-.75.4-1.25.73-1.54-2.55-.29-5.23-1.28-5.23-5.68 0-1.25.45-2.28 1.19-3.08-.12-.29-.52-1.46.11-3.04 0 0 .97-.31 3.17 1.18a10.9 10.9 0 0 1 5.78 0c2.2-1.49 3.16-1.18 3.16-1.18.63 1.58.23 2.75.11 3.04.74.8 1.19 1.83 1.19 3.08 0 4.41-2.69 5.38-5.25 5.67.42.36.78 1.06.78 2.14v3.18c0 .31.21.68.8.56A11.51 11.51 0 0 0 23.5 12C23.5 5.65 18.35.5 12 .5Z" />
            </svg>
        );

    if (icon === "discord")
        return (
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M19.54 5.34A18.8 18.8 0 0 0 14.92 3.9l-.22.44a17.3 17.3 0 0 1 4.14 2.06 13.42 13.42 0 0 0-12.37 0 16.6 16.6 0 0 1 4.14-2.06l-.22-.44a18.8 18.8 0 0 0-4.63 1.44C2.83 9.72 2.03 13.99 2.43 18.2a18.9 18.9 0 0 0 5.67 2.87l.69-.94a12.2 12.2 0 0 1-1.09-.52c.31-.12.62-.25.91-.4a13.44 13.44 0 0 0 6.78 0c.3.15.6.28.91.4-.35.19-.71.36-1.09.52l.69.94a18.85 18.85 0 0 0 5.68-2.87c.47-4.87-.8-9.1-2.04-12.86ZM8.52 15.62c-1.1 0-2-1.01-2-2.25s.88-2.26 2-2.26c1.12 0 2.02 1.02 2 2.26 0 1.24-.88 2.25-2 2.25Zm6.96 0c-1.1 0-2-1.01-2-2.25s.88-2.26 2-2.26c1.12 0 2.02 1.02 2 2.26 0 1.24-.88 2.25-2 2.25Z" />
            </svg>
        );

    return (
        <svg class="landing-footer-social-icon-topgg" viewBox="120 120 580 580" aria-hidden="true">
            <path fill="currentColor" d="M655.711 247H330.71V572H397.113C422.599 572 447.042 561.876 465.064 543.854C483.086 525.832 493.21 501.389 493.21 475.902V409.5H559.613C585.099 409.5 609.542 399.375 627.564 381.354C645.586 363.332 655.711 338.889 655.711 313.402V247Z" />
            <path fill="currentColor" d="M144 247H306.5V409.5H193.657C180.531 409.5 167.943 404.286 158.661 395.004C149.379 385.722 144.165 373.134 144.165 360.008L144 247Z" />
        </svg>
    );
}

export function usePublicLocalizedPath() {
    const route = useRoute();

    return (path: string) => {
        const localeCode = typeof route.params["locale"] === "string"
            ? route.params["locale"] as PublicLocaleCode
            : null;

        return localeCode
            ? withPublicLocalePrefix(path, localeCode)
            : path;
    };
}

export const PublicNavbar = defineComponent({
    setup() {
        const { i18n } = useLocale();
        const publicPath = usePublicLocalizedPath();
        const route = useRoute();
        const menuOpen = ref(false);
        const moreMenuOpen = ref(false);
        const moreMenu = ref<HTMLElement | null>(null);
        const moreMenuButton = ref<HTMLButtonElement | null>(null);
        const hamburgerButton = ref<HTMLButtonElement | null>(null);

        const avatarUrl = computed(() => {
            if (!Store.me?.id)
                return null;

            if (Store.me.avatar)
                return `https://cdn.discordapp.com/avatars/${Store.me.id}/${Store.me.avatar}.png?size=64`;

            const avatar = Number((BigInt(Store.me.id) >> 22n) % 6n);

            return `https://cdn.discordapp.com/embed/avatars/${avatar}.png`;
        });

        const isConnected = computed(() => Store.me !== null);

        const authLabel = computed(() => (
            isConnected.value
                ? i18n("public.nav.my_profile")
                : i18n("public.cta.use")
        ));

        const authActionLabel = computed(() => (
            isConnected.value
                ? i18n("public.nav.my_profile")
                : i18n("public.nav.connect_short")
        ));

        const authTarget = computed(() => (
            isConnected.value
                ? "/profile"
                : "/api/oauth2"
        ));

        const isLoggedAction = computed(() => isConnected.value);

        const secondaryRouteActive = computed(() => PublicSecondaryLinks.some((link) => {
            const target = publicPath(link.to);

            return route.path === target || route.path.startsWith(`${target}/`);
        }));

        function closeMenu() {
            menuOpen.value = false;
            moreMenuOpen.value = false;
        }

        function toggleMenu() {
            menuOpen.value = !menuOpen.value;
        }

        function toggleMoreMenu() {
            moreMenuOpen.value = !moreMenuOpen.value;
        }

        function handleDocumentPointerDown(event: PointerEvent) {
            if (moreMenu.value && !moreMenu.value.contains(event.target as Node))
                moreMenuOpen.value = false;
        }

        function handleDocumentKeyDown(event: KeyboardEvent) {
            if (event.key !== "Escape")
                return;

            if (moreMenuOpen.value) {
                moreMenuOpen.value = false;
                moreMenuButton.value?.focus();
            }
            else if (menuOpen.value) {
                menuOpen.value = false;
                hamburgerButton.value?.focus();
            }
        }

        watch(menuOpen, (isOpen) => {
            document.documentElement.classList.toggle("public-menu-open", isOpen);
        });

        onMounted(() => {
            document.addEventListener("pointerdown", handleDocumentPointerDown);
            document.addEventListener("keydown", handleDocumentKeyDown);
        });

        onBeforeUnmount(() => {
            document.removeEventListener("pointerdown", handleDocumentPointerDown);
            document.removeEventListener("keydown", handleDocumentKeyDown);
            document.documentElement.classList.remove("public-menu-open");
        });

        return () => (
            <div class={{ "public-shell-nav": true, "mobile-menu-open": menuOpen.value }}>
                <router-link class="landing-brand" to={publicPath("/") } aria-label="LogsCord">
                    <img class="landing-brand-mark" src="/assets/img/icon.svg" alt="" aria-hidden="true" />
                    <span>LOGSCORD</span>
                </router-link>

                <div class="public-shell-links">
                    <div
                        class={{
                            "public-shell-more": true,
                            active: secondaryRouteActive.value,
                            open: moreMenuOpen.value,
                        }}
                        ref={moreMenu}
                    >
                        <button
                            ref={moreMenuButton}
                            type="button"
                            aria-controls="public-shell-discover-menu"
                            aria-expanded={moreMenuOpen.value}
                            onClick={toggleMoreMenu}
                        >
                            <i class="material-symbols-outlined">explore</i>
                            <span>{i18n("public.nav.discover")}</span>
                            <i class="material-symbols-outlined public-shell-more-chevron">expand_more</i>
                        </button>

                        {moreMenuOpen.value && (
                            <div
                                id="public-shell-discover-menu"
                                class="public-shell-more-menu"
                                aria-label={i18n("public.nav.discover")}
                            >
                                {PublicSecondaryLinks.map((link) => (
                                    <router-link
                                        to={publicPath(link.to)}
                                        onClick={closeMenu}
                                        key={link.to}
                                    >
                                        <i class="material-symbols-outlined">{link.icon}</i>
                                        <span>{i18n(link.labelKey)}</span>
                                    </router-link>
                                ))}
                            </div>
                        )}
                    </div>

                    {PublicPrimaryLinks.map((link) => (
                        <router-link to={publicPath(link.to)} onClick={closeMenu} key={link.to}>
                            <i class="material-symbols-outlined">{link.icon}</i>
                            <span>{i18n(link.labelKey)}</span>
                        </router-link>
                    ))}
                </div>

                <div class="landing-nav-actions">
                    <PwaInstallButton />

                    {isConnected.value
                        ? (
                            <router-link class="landing-button landing-button-dark public-shell-profile-link" to="/profile">
                                {avatarUrl.value
                                    ? <img src={avatarUrl.value} alt="Profile" class="public-shell-profile-avatar" />
                                    : <i class="material-symbols-outlined">person</i>}

                                {authLabel.value}
                            </router-link>
                        )
                        : (
                            <a class="landing-button landing-button-dark" href={authTarget.value}>
                                {authLabel.value}
                                <i class="material-symbols-outlined">arrow_forward</i>
                            </a>
                        )}
                </div>

                <button
                    ref={hamburgerButton}
                    class={{
                        "public-shell-hamburger": true,
                        open: menuOpen.value,
                    }}
                    type="button"
                    aria-label="Menu"
                    aria-expanded={menuOpen.value}
                    onClick={toggleMenu}
                >
                    <span />
                    <span />
                    <span />
                </button>

                <div
                    class={{ "public-shell-mobile-menu": true, open: menuOpen.value }}
                    role="dialog"
                    aria-modal="true"
                    aria-hidden={!menuOpen.value}
                    aria-label="Menu"
                >
                    <div class="public-shell-mobile-header">
                        <router-link class="landing-brand" to={publicPath("/")} aria-label="LogsCord" onClick={closeMenu}>
                            <img class="landing-brand-mark" src="/assets/img/icon.svg" alt="" aria-hidden="true" />
                            <span>LOGSCORD</span>
                        </router-link>

                        <button
                            class="public-shell-mobile-close"
                            type="button"
                            aria-label={i18n("public.nav.close_menu")}
                            onClick={closeMenu}
                        >
                            <i class="material-symbols-outlined">close</i>
                        </button>
                    </div>

                    <div class="public-shell-mobile-links">
                        <router-link
                            class="public-shell-mobile-link"
                            to={publicPath("/")}
                            onClick={closeMenu}
                        >
                            <i class="material-symbols-outlined">monitoring</i>
                            <span>{i18n("navbar.home.label")}</span>
                        </router-link>

                        {PublicLinks.map((link) => (
                            <router-link to={publicPath(link.to)} onClick={closeMenu} key={link.to}>
                                <i class="material-symbols-outlined">{link.icon}</i>
                                <span>{i18n(link.labelKey)}</span>
                            </router-link>
                        ))}
                    </div>

                    <div class="public-shell-mobile-auth">
                        {isLoggedAction.value
                            ? <router-link class="landing-button landing-button-dark" to={authTarget.value} onClick={closeMenu}>
                                {avatarUrl.value
                                    ? <img src={avatarUrl.value} alt="Profil" class="public-shell-profile-avatar" />
                                    : <i class="material-symbols-outlined">person</i>}

                                {authActionLabel.value}
                            </router-link>
                            : <a class="landing-button landing-button-dark" href={authTarget.value} onClick={closeMenu}>
                                {authActionLabel.value}
                                <i class="material-symbols-outlined">arrow_forward</i>
                            </a>
                        }

                        <PwaInstallButton buttonClass="landing-button landing-button-install" />
                    </div>
                </div>
            </div>
        );
    },
});

export const PublicFooter = defineComponent({
    setup() {
        const { i18n } = useLocale();
        const publicPath = usePublicLocalizedPath();

        return () => (
            <div class="public-shell-footer">
                <div class="landing-footer-brand">
                    <router-link class="landing-brand" to={publicPath("/")} aria-label="LogsCord">
                        <img class="landing-brand-mark" src="/assets/img/icon.svg" alt="" aria-hidden="true" />
                        <span>LOGSCORD</span>
                    </router-link>

                    <p>{i18n("public.footer.slogan")}</p>

                    <div class="landing-footer-socials" aria-label="Liens sociaux LogsCord">
                        {PublicSocialLinks.map((link) => (
                            <a href={link.href} target="_blank" rel="noreferrer" aria-label={link.label} title={link.label} key={link.href}>
                                {renderSocialIcon(link.icon)}
                            </a>
                        ))}
                    </div>
                </div>

                <nav class="landing-footer-links" aria-label={i18n("public.footer.navigation")}>
                    {PublicFooterSections.map((section) => (
                        <div class="landing-footer-section" key={section.labelKey}>
                            <span class="landing-footer-section-title">{i18n(section.labelKey)}</span>

                            <div class="landing-footer-section-links">
                                {section.links.map((link) => (
                                    <router-link to={publicPath(link.to)} key={link.to}>
                                        <i class="material-symbols-outlined">{link.icon}</i>
                                        <span>{i18n(link.labelKey)}</span>
                                    </router-link>
                                ))}
                            </div>
                        </div>
                    ))}
                </nav>

                <a class="landing-button landing-button-dark" href="/api/oauth2">
                    {i18n("public.cta.use")}
                    <i class="material-symbols-outlined">arrow_forward</i>
                </a>
            </div>
        );
    },
});

export const LandingTimelinePreview = defineComponent({
    setup() {
        const { i18n } = useLocale();

        return () => (
            <div class="landing-product-shot" aria-label={i18n("public.preview.aria")}>
                <div class="landing-app-shell">
                    <div class="landing-app-sidebar">
                        <div class="landing-app-logo">
                            <span class="landing-app-logo-mark">L</span>
                            <span>LOGSCORD</span>
                        </div>

                        {LogscordLandingPreviewSidebarItems.map((item) => (
                            <span class={{ active: item.active === true }}>
                                <i class="material-symbols-outlined">{item.icon}</i>
                                <span>{i18n(item.labelKey)}</span>
                            </span>
                        ))}
                    </div>

                    <div class="landing-app-panel">
                        <div class="landing-app-header">
                            <div>
                                <strong>{i18n("public.preview.title")}</strong>
                                <small>{i18n("public.preview.subtitle")}</small>
                            </div>

                            <div class="landing-search">
                                <i class="material-symbols-outlined">search</i>
                                <span>{i18n("public.preview.search")}</span>
                            </div>
                        </div>

                        <div class="landing-preview-metrics">
                            {LogscordLandingPreviewMetrics.map((metric) => (
                                <div>
                                    <i class="material-symbols-outlined">{metric.icon}</i>
                                    <span>{metric.value}</span>
                                    <small>{i18n(metric.labelKey)}</small>
                                </div>
                            ))}
                        </div>

                        <div class="landing-timeline-preview">
                            {LogscordLandingTimelineRows.map((row) => (
                                <div class="landing-timeline-row">
                                    <time>{row.time}</time>
                                    <span class={`landing-type landing-type-${row.type}`}>
                                        <i class="material-symbols-outlined">{row.icon}</i>
                                    </span>
                                    <p>{i18n(row.textKey)}</p>
                                    <b>{i18n(row.tagKey)}</b>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    },
});
